import classNames from 'classnames'
import React, { ReactElement } from 'react'

import { GridColumn } from '../../grid/grid-column.js'
import { Grid } from '../../grid/grid.js'
import { OptionCard, OptionCardProps } from '../option-card/option-card.js'

export type OptionCardGroupValue = string | string[] | null

export interface OptionCardGroupProps {
    children: React.ReactNode
    id: string
    inputType: 'radio' | 'checkbox'
    name: string
    className?: string
    value?: OptionCardGroupValue
    onChange?: (value: OptionCardGroupValue) => void
}

export const OptionCardGroup = (props: OptionCardGroupProps): JSX.Element => {
    const { children } = props

    const className: string = classNames('option-card-group', props.className)

    const handleChange = (val: string, checked: boolean, onChange?: (checked: boolean) => void): void => {
        let newValue = props.value

        if (props.inputType === 'checkbox') {
            newValue = newValue || []

            if (Array.isArray(newValue)) {
                if (checked && !newValue.includes(val)) {
                    newValue.push(val)
                } else {
                    newValue = newValue.filter((item) => item !== val)
                }
            }
        } else {
            newValue = val
        }

        if (props.onChange) {
            props.onChange(newValue)
        }

        if (onChange) {
            onChange(checked)
        }
    }

    const isChecked = (val: string): boolean => {
        const currentValue = props.value

        if (props.inputType === 'checkbox' && currentValue) {
            return val === currentValue || currentValue.indexOf(val) !== -1
        } else {
            return val === currentValue
        }
    }

    const getOptionInput = (option: OptionCardProps): React.InputHTMLAttributes<HTMLInputElement> => {
        return {
            ...option.input,
            name: props.name,
        }
    }

    const renderOptionCard = (child: React.ReactElement<OptionCardProps>) => {
        const childProps = child.props
        const val = childProps.value || 'on'
        const onChange = (checked: boolean) => handleChange(val, checked, childProps.onChange)

        return (
            <OptionCard
                {...childProps}
                checked={isChecked(val)}
                input={getOptionInput(childProps)}
                inputType={props.inputType}
                onChange={onChange}
            />
        )
    }

    const renderChildren = () => {
        return React.Children.toArray(children)
            .filter((child): child is React.ReactElement => {
                return React.isValidElement(child)
            })
            .map((child, key: number) => {
                const childProps = child.props as OptionCardProps

                return (
                    <GridColumn width={[childProps.width ? childProps.width : 'sm-6']} key={key}>
                        {renderOptionCard(child as ReactElement<OptionCardProps>)}
                    </GridColumn>
                )
            })
    }

    return (
        <Grid className={className} equalHeight={true}>
            {renderChildren()}
        </Grid>
    )
}
